import mixpanel from 'mixpanel-browser';

import { useAuthStore } from '@/stores/auth';
import { useGlobalStore } from '@/stores/global';

const PRODUCTION_PROJECT_KEY = '22b9d6e0493fe586ad81553ebc0ef1fb';
const TESTS_PROJECT_KEY = '22eb0f7089b72267f1eda2edc7220493';

/**
 * Reporter singleton
 *
 */
const Reporter = (() => {
  let instance;

  const constructor = function () {
    const authStore = useAuthStore();
    const globalStore = useGlobalStore();

    const email = authStore.getFullUser?.email;
    const userUUID = authStore.getFullUser?.uuid;
    const company_name = authStore.getCompany.label;
    const company_plan = authStore.getCompany.plan;
    const customerUUID = authStore.getCustomerUUID;

    const env = globalStore.getEnvironment;
    const proxyUrl = globalStore.getMixpanelProxy;

    this.enableDevLogs = false; // Update this variable to enable logs in DEV
    this.isDevEnv = env === 'dev';

    this.canLog = !this.isDevEnv || this.enableDevLogs;
    this.canDebug = this.isDevEnv && this.enableDevLogs;

    // set TS session start time
    this.sessionStartTime = new Date().getTime();

    // set project key to use, depends of execution environment
    const projectKey = env === 'prod' ? PRODUCTION_PROJECT_KEY : TESTS_PROJECT_KEY;

    /**
     * Public function allowing to report data
     */
    this.reportData = function (event, params = {} as any) {
      if (this.canLog) {
        // for each report ...
        //
        // inject session duration
        const now = new Date().getTime();
        params.session_duration = (now - this.sessionStartTime) / 1000;

        // inject email, company name & cie
        params.email = email;
        params.user_uuid = userUUID;
        params.company_name = company_name;
        params.plan = company_plan;
        params.customer_uuid = customerUUID;

        mixpanel.track(event, params);
      }
    };

    this.startTimeEvent = function (event) {
      if (this.canLog) {
        mixpanel.time_event(event);
      }
    };

    // 'debug' will add some logs in console to know what is sent
    if (this.canLog) {
      mixpanel.init(projectKey, {
        debug: this.canDebug,
        api_host: proxyUrl
      });

      // identify user with their UUID
      mixpanel.identify(userUUID);

      mixpanel.set_config({
        batch_flush_interval_ms: 1000
      });
    }
  };

  /**
   * Create Reporter instance
   */
  const createInstance = () => {
    instance = new constructor();
    instance.constructor = null;

    return instance;
  };

  return () => {
    // Check if instance already exists
    return instance || createInstance();
  };
})();

export default Reporter;
